import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { getAdminHosts, approveHost, declineHost } from '../../lib/api';
import { toast } from 'sonner';
import { Loader2, Check, X, CreditCard } from 'lucide-react';

export default function AdminHosts() {
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  
  // Decline dialog
  const [declineDialogOpen, setDeclineDialogOpen] = useState(false);
  const [selectedHost, setSelectedHost] = useState(null);
  const [declineReason, setDeclineReason] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    loadHosts();
  }, [statusFilter]);

  const loadHosts = async (cursor = null) => {
    setLoading(true);
    try {
      const params = { limit: 20 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (cursor) params.cursor = cursor;
      
      const res = await getAdminHosts(params);
      if (cursor) {
        setHosts(prev => [...prev, ...res.data.items]);
      } else {
        setHosts(res.data.items || []);
      }
      setNextCursor(res.data.next_cursor);
      setHasMore(res.data.has_more);
    } catch (err) {
      toast.error('Failed to load hosts');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (hostId) => {
    setProcessing(true);
    try {
      await approveHost(hostId);
      toast.success('Host approved');
      setHosts(prev => prev.map(h => 
        h.id === hostId ? { ...h, host_status: 'approved' } : h
      ));
    } catch (err) {
      toast.error('Failed to approve host');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeclineClick = (host) => {
    setSelectedHost(host);
    setDeclineReason('');
    setDeclineDialogOpen(true);
  };

  const handleDeclineConfirm = async () => {
    if (!selectedHost) return;
    setProcessing(true);
    try {
      await declineHost(selectedHost.id, declineReason || null);
      toast.success('Host declined');
      setHosts(prev => prev.map(h => 
        h.id === selectedHost.id ? { ...h, host_status: 'declined' } : h
      ));
      setDeclineDialogOpen(false);
    } catch (err) {
      toast.error('Failed to decline host');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-700',
      approved: 'bg-green-100 text-green-700',
      declined: 'bg-red-100 text-red-700',
      suspended: 'bg-slate-100 text-slate-700'
    };
    return (
      <Badge className={styles[status] || styles.pending}>
        {status || 'pending'}
      </Badge>
    );
  };

  const getStripeStatus = (host) => {
    const status = host.stripe_onboarding_status || 'not_connected';
    
    if (status === 'not_connected' || !host.stripe_account_id_masked) {
      return <Badge variant="outline" className="text-slate-500">No Stripe</Badge>;
    }
    if (status === 'verified') {
      return (
        <div className="flex flex-col gap-1">
          <Badge className="bg-green-100 text-green-700">
            <CreditCard className="w-3 h-3 mr-1" />Verified
          </Badge>
          <span className="text-xs text-slate-400">{host.stripe_account_id_masked}</span>
        </div>
      );
    }
    if (status === 'restricted') {
      return (
        <div className="flex flex-col gap-1">
          <Badge className="bg-red-100 text-red-700">
            <CreditCard className="w-3 h-3 mr-1" />Restricted
          </Badge>
          <span className="text-xs text-slate-400">{host.stripe_account_id_masked}</span>
        </div>
      );
    }
    // pending
    return (
      <div className="flex flex-col gap-1">
        <Badge className="bg-yellow-100 text-yellow-700">
          <CreditCard className="w-3 h-3 mr-1" />Pending
        </Badge>
        <span className="text-xs text-slate-400">{host.stripe_account_id_masked}</span>
      </div>
    );
  };

  if (loading && hosts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-hosts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Host Management</h1>
          <p className="text-slate-500">Approve, decline, or suspend hosts</p>
        </div>
        
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setHosts([]); }}>
          <SelectTrigger className="w-40" data-testid="status-filter">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Hosts</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="approved">Approved</SelectItem>
            <SelectItem value="declined">Declined</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium text-slate-600">Host</th>
                  <th className="text-left p-4 font-medium text-slate-600">Status</th>
                  <th className="text-left p-4 font-medium text-slate-600">Stripe</th>
                  <th className="text-left p-4 font-medium text-slate-600">Joined</th>
                  <th className="text-right p-4 font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {hosts.map(host => (
                  <tr key={host.id} className="hover:bg-slate-50">
                    <td className="p-4">
                      <div>
                        <p className="font-medium">{host.name}</p>
                        <p className="text-sm text-slate-500">{host.email}</p>
                      </div>
                    </td>
                    <td className="p-4">{getStatusBadge(host.host_status)}</td>
                    <td className="p-4">{getStripeStatus(host)}</td>
                    <td className="p-4 text-sm text-slate-500">
                      {new Date(host.created_at).toLocaleDateString()}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {(host.host_status === 'pending' || !host.host_status) && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleApprove(host.id)}
                              disabled={processing}
                              data-testid={`approve-btn-${host.id}`}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="destructive"
                              onClick={() => handleDeclineClick(host)}
                              disabled={processing}
                              data-testid={`decline-btn-${host.id}`}
                            >
                              <X className="w-4 h-4 mr-1" />
                              Decline
                            </Button>
                          </>
                        )}
                        {host.host_status === 'declined' && (
                          <Button
                            size="sm"
                            onClick={() => handleApprove(host.id)}
                            disabled={processing}
                          >
                            Re-approve
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {hosts.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-500">
              No hosts found
            </div>
          )}
        </CardContent>
      </Card>

      {hasMore && (
        <div className="text-center">
          <Button variant="outline" onClick={() => loadHosts(nextCursor)} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Load More
          </Button>
        </div>
      )}

      {/* Decline Dialog */}
      <Dialog open={declineDialogOpen} onOpenChange={setDeclineDialogOpen}>
        <DialogContent data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle>Decline Host Application</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-600 mb-4">
              Are you sure you want to decline <strong>{selectedHost?.name}</strong>?
            </p>
            <Textarea
              placeholder="Reason for declining (optional)"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              rows={3}
              data-testid="decline-reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeclineDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeclineConfirm}
              disabled={processing}
              data-testid="confirm-decline-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Decline Host
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
